import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
    Button,
    Card,
    CardBody,
    CardError,
    CardFooter,
    CardHeader,
    MaskedInput,
    Paragraph
} from 'view/components/common';
import { isRequired } from 'core/utilities';
import { resetPassword, useAuthorizationDispatch } from 'core/context';
import { useKeypress } from 'view/hooks';
import { Spinner } from 'view/components/layout';
import { useToast } from 'core/toast';

export const ResetPasswordPage : React.FunctionComponent = () => 
{
    const history = useHistory();
    const dispatch = useAuthorizationDispatch();
    const { setToastMessage } = useToast();
    const [ submitError, setSubmitError ] = useState<string | undefined>();
    const [ status,      setStatus      ] = useState<boolean>(false);
    const [ error,       setError       ] = useState<string | undefined>();
    const [ isLoading,   setIsLoading   ] = useState<boolean>(false);
    const [ submitted,   setSubmitted   ] = useState<boolean>(false);
    const [ canContinue, setCanContinue ] = useState<boolean>(false);
    const { register, setValue, getValues } = useForm({ mode : 'onChange' });

    const parameters = window.location.search;
    const [ token, email ] = parameters
        .replace('?token=', ' ')
        .replace('&email=', ' ')
        .replace('&new_user=', '')
        .split(' ')
        .slice(1);

    // Query params `token` and `email` must be set
    if (token === null || email === null) history.push('/login');

    const onBack = () => history.push('/login');
    const onSubmit = async () => 
    {
        if (canContinue && !isLoading)
        {
            setIsLoading(true);
            setCanContinue(false);

            const { password, confirm } = getValues();

            if (password === confirm)
            {
                const payload = {
                    email,
                    token,
                    password
                };
                const { success, error } = await resetPassword({ dispatch, payload });
    
                setIsLoading(false);
                setCanContinue(true);
                setStatus(success);
                setError(error);
        
                if (success && typeof error === 'string')
                {
                    // Reset succeeded, but auto-login failed
                    setSubmitError(undefined);
                    setSubmitted(true);
                }
                else 
                {
                    // Reset failed
                    setSubmitError(error);
                    if (error === undefined) setToastMessage();
                }
            }
            else 
            {
                setIsLoading(false);
                setCanContinue(true);
                setError('Passwords do not match, please try again.');
            }
        }
    };

    useEffect(() => 
    {
        // Reset & login succeeded. Redirect user to dashboard.
        if (status && !!error) history.push('/dashboard');
    }, [ status, error ]);

    useEffect(() => 
    {   // Continuously revalidate form values
        const { password, confirm } = getValues();
        setCanContinue(password === confirm);
    }, [ getValues ]);

    useKeypress('Enter', onSubmit);

    return (
        <Container>
            <Card height={480}>
                <CardHeader 
                    content='Reset Password'
                    css='margin-top: 16px; padding-bottom: 20px;'
                    backHandler={onBack}
                />
                {submitError && (
                    <CardError
                        content={submitError}
                        css={'margin-bottom: -5px; margin-top: -10px;'}
                    />
                )}
                <CardBody>
                    {isLoading ? (
                        <Spinner css='height: inherit; margin-top: 50px;' />
                    ) : submitted ? (
                        <Paragraph> 
                            Password reset successfully! Please proceed to the login page to use your new password.
                        </Paragraph>
                    ) :(
                        <>
                            <MaskedInput
                                name='password'
                                label='Password'
                                placeholder='*******************'
                                validator={isRequired}
                                setValue={setValue}
                                input={register('password')}
                            />
                            <MaskedInput
                                name='confirm'
                                label='Confirm Password'
                                placeholder='*******************'
                                validator={isRequired}
                                setValue={setValue}
                                input={register('confirm')}
                            />
                        </>
                    )}
                </CardBody>
                <CardFooter>
                    <Button
                        title='Reset'
                        type='button'
                        disabled={!canContinue || isLoading}
                        onClick={onSubmit}
                        css='text-transform: none; padding-bottom: 3px;'
                        arrowCss='padding-left: 5px; margin-top: 5px;'
                    />
                </CardFooter>
            </Card>
        </Container>
    );
};

const Container = styled.div`
    position: relative;
`;